// Grupo raíz de Keycloak cuyos subgrupos determinan el rol de la aplicación.
// El claim `groups` del token trae la ruta completa (p. ej. `/Bienes Fiscales/ITDirector`).
export const BIENES_FISCALES_GROUP_PATH = '/Bienes Fiscales/';

// Equivalencia subgrupo de `/Bienes Fiscales` -> rol de la aplicación (mismo nombre que la tabla `roles` del backend).
// El orden es la jerarquía: ante varios subgrupos gana el primero. Copia de
// `modules/users/constants/keycloak-groups.ts` del backend; los dos deben mantenerse iguales.
export const ROLE_BY_KEYCLOAK_SUBGROUP: ReadonlyArray<readonly [string, string]> = [
	['Super_Admin', 'Super_Admin'],
	['ITDirector', 'Director_de_Informática'],
	['Director_de_BienesFiscales', 'Director_de_Bienes_Fiscales'],
	['Operador_Dir_de_BienesFiscales', 'Operador_de_Dirección_de_Bienes_Fiscales'],
	['Responsable_Patrimonial', 'Encargado_Patrimonial'],
	['Operador_Dependencia_Patrimonial', 'Operador_de_Dependencia_Patrimonial']
];

// Roles de dirección: entran sin dependencia patrimonial. Los otros dos la exigen.
export const DIRECTION_ROLES: readonly string[] = [
	'Super_Admin',
	'Director_de_Informática',
	'Director_de_Bienes_Fiscales',
	'Operador_de_Dirección_de_Bienes_Fiscales'
];

// Compara roles sin importar tildes, mayúsculas ni guiones bajos/espacios.
export const normalizeRole = (role: string): string =>
	role
		.normalize('NFD')
		.replace(/[̀-ͯ]/g, '')
		.replace(/[_\s]+/g, '_')
		.toLowerCase();

// Deduce el rol de la aplicación del claim `groups`. Ignora grupos ajenos a `/Bienes Fiscales/` y subgrupos
// desconocidos; devuelve `null` si no hay ninguno reconocido.
export const resolveRoleFromGroups = (groups: unknown): string | null => {
	if (!Array.isArray(groups)) return null;

	const subgroups = new Set<string>(
		groups
			.filter((group): group is string => typeof group === 'string' && group.startsWith(BIENES_FISCALES_GROUP_PATH))
			.map((group) => group.slice(BIENES_FISCALES_GROUP_PATH.length))
	);

	const match = ROLE_BY_KEYCLOAK_SUBGROUP.find(([subgroup]) => subgroups.has(subgroup));
	return match ? match[1] : null;
};

// Claims del token que pueden traer la pertenencia a grupos. `groups` es el del mapper Group Membership, pero en los
// ambientes con el scope `roles` estándar ese claim lo pisa un mapper de roles de realm; ahí la ruta de los grupos
// llega en `grupos`. Se leen los dos. Copia de `GROUP_CLAIMS` del backend.
export const GROUP_CLAIMS = ['groups', 'grupos'] as const;

// Deduce el rol de la aplicación juntando los grupos de todos los claims de `GROUP_CLAIMS` del token.
export const resolveRoleFromToken = (token: Record<string, unknown> | null | undefined): string | null =>
	resolveRoleFromGroups(
		GROUP_CLAIMS.flatMap((claim) => {
			const value = token?.[claim];
			return Array.isArray(value) ? value : [];
		})
	);

export const isDirectionRole = (role: string | null | undefined): boolean =>
	!!role && DIRECTION_ROLES.map(normalizeRole).includes(normalizeRole(role));

// Nombre legible de un rol para mostrar en pantalla (`Director_de_Informática` -> `Director de Informática`).
export const roleLabel = (role: string): string => role.replace(/_/g, ' ');

// ¿Tiene la persona alguno de los roles dados? Compara normalizado (ver `normalizeRole`).
export const hasAnyRole = (userRoles: readonly string[], roles: readonly string[]): boolean => {
	const normalized = userRoles.map(normalizeRole);
	return roles.some((role) => normalized.includes(normalizeRole(role)));
};
